import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const NetworkChartRenderer = ({ data, config = {} }) => {
  const svgRef = useRef();

  useEffect(() => {
    if (!data) return;
    try {
      renderNetwork();
    } catch (error) {
      console.error('Network chart rendering error:', error);
    }
  }, [data, config]);

  const renderNetwork = () => {
    const svgElement = svgRef.current;
    if (!svgElement) return;
    
    d3.select(svgElement).selectAll('*').remove();

    const width = config.width || 800;
    const height = config.height || 600;

    const svg = d3.select(svgElement)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    const g = svg.append('g');

    // Zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Validate data
    if (!data || !data.nodes || !data.edges) {
      console.error('Invalid network data:', data);
      return;
    }

    // Ensure all nodes have IDs
    const nodes = data.nodes.map((n, i) => ({
      ...n,
      id: n.id || `node-${i}`,
      label: n.label || n.name || `Node ${i + 1}`,
    }));

    // Validate edges and filter out invalid ones
    const edges = data.edges.filter(e => {
      const sourceExists = nodes.some(n => n.id === e.from || n.id === e.source);
      const targetExists = nodes.some(n => n.id === e.to || n.id === e.target);
      if (!sourceExists || !targetExists) {
        console.warn('Invalid edge:', e);
        return false;
      }
      return true;
    }).map(e => ({
      source: e.from || e.source,
      target: e.to || e.target,
    }));

    // Force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(edges).id(d => d.id).distance(150))
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(50));

    // Links
    const link = g.append('g')
      .selectAll('line')
      .data(edges)
      .enter()
      .append('line')
      .attr('stroke', '#667eea')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 2);

    // Nodes
    const node = g.append('g')
      .selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('r', 25)
      .attr('fill', '#667eea')
      .attr('stroke', 'white')
      .attr('stroke-width', 3)
      .style('cursor', 'grab')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    // Labels
    const label = g.append('g')
      .selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .text(d => d.label)
      .attr('font-size', 13)
      .attr('font-weight', 'bold')
      .attr('dx', 30)
      .attr('dy', 4)
      .attr('fill', '#333')
      .style('pointer-events', 'none');

    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node
        .attr('cx', d => d.x)
        .attr('cy', d => d.y);

      label
        .attr('x', d => d.x)
        .attr('y', d => d.y);
    });

    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
      <svg ref={svgRef} style={{ border: '1px solid #f0f0f0', borderRadius: '4px', maxWidth: '100%', height: 'auto' }} />
    </div>
  );
};

export default NetworkChartRenderer;
